import {
  faCheckCircle,
  faDollar,
  faImage,
  faLock,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  FiatWalletAccountState,
  SCAEnrollmentState,
} from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import type { IconDefinition } from '#src/atoms/icons/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  HeadlineL,
  LabelM,
  LabelS,
} from '#src/atoms/typography/index.tsx';
import { NOT_SUPPORTED_COUNTRIES_LINK } from '#src/components/fiatWallet/CreateFiatWallet/externalLinks.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { glossary } from '#src/lib/glossary.ts';

const ItemRoot = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--unit);
  padding: var(--intermediate-unit);
  background-color: var(--c-nd-100);
  border-radius: var(--unit);
`;

const ItemContent = styled.div`
  display: flex;
  gap: var(--unit);
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  border-radius: var(--triple-unit);
  background-color: var(--c-nd-400);
  padding: var(--unit);
  aspect-ratio: 1 / 1;
`;

const StyledChecked = styled(FontAwesomeIcon)`
  margin-left: var(--unit);
`;
type ItemProps = {
  title: ReactNode;
  desc: ReactNode;
  icon: IconDefinition;
  step: number;
  checked?: boolean;
};
const Item = ({ title, desc, icon, step, checked }: ItemProps) => (
  <ItemRoot>
    <ItemContent>
      <BodyM bold color="var(--c-white)">
        {step}
      </BodyM>
      <div>
        {!!title && (
          <BodyM bold color="var(--c-white)">
            {title}
            {!!checked && (
              <StyledChecked
                icon={faCheckCircle}
                color="var(--c-onBase-success)"
              />
            )}
          </BodyM>
        )}
        {!!desc && <LabelM color="var(--c-nd-600)">{desc}</LabelM>}
      </div>
    </ItemContent>
    <StyledFontAwesomeIcon icon={icon} size="sm" color="var(--c-white)" />
  </ItemRoot>
);

const Content = styled(Vertical).attrs({ gap: 2 })`
  height: 100%;
`;
const LinkInFormattedMessage = styled.a`
  text-decoration: underline;
  color: var(--c-link);
`;

const Actions = styled(Vertical).attrs({ gap: 0 })`
  flex-grow: 1;
  width: 100%;
  justify-content: flex-end;
`;
type Props = {
  statusTarget: FiatWalletAccountState;
  onGetStarted: () => void;
  canDismissAfterActivation?: boolean;
};

type Step = {
  title: ReactNode;
  desc: ReactNode;
  icon: IconDefinition;
  checked?: boolean;
};

export const Intro = ({
  statusTarget,
  canDismissAfterActivation,
  onGetStarted,
}: Props) => {
  const {
    flags: { enableMangopaySca = false },
  } = useFeatureFlags();
  const { currentUser } = useCurrentUserContext();
  const { canListAndTrade, isFiatWalletOwner } = useFiatBalance();
  const fromListing = statusTarget === FiatWalletAccountState.OWNER;
  const fromOnboarding =
    statusTarget === FiatWalletAccountState.VALIDATED_OWNER &&
    canDismissAfterActivation;
  const fromDepositAndWithdraw =
    statusTarget === FiatWalletAccountState.VALIDATED_OWNER &&
    !canDismissAfterActivation;

  const canSkipScaStep =
    canListAndTrade &&
    currentUser?.mangopayUser?.scaEnrollmentState ===
      SCAEnrollmentState.SCA_NOT_ENROLLED;

  const Steps: Step[] = [
    {
      title: <FormattedMessage defaultMessage="Share some basic info" />,
      desc: (
        <FormattedMessage defaultMessage="Share your full name, date of birth, nationality and country of residence." />
      ),
      icon: faUser,
      checked: isFiatWalletOwner,
    },
    {
      title: <FormattedMessage defaultMessage="Choose a currency" />,
      desc: (
        <FormattedMessage defaultMessage="Select your preferred currency between USD, EUR, or GBP." />
      ),
      icon: faDollar,
      checked: isFiatWalletOwner,
    },
    enableMangopaySca &&
      !canSkipScaStep && {
        title: (
          <FormattedMessage defaultMessage="Enroll in strong authentication with Mangopay" />
        ),
        desc: (
          <FormattedMessage defaultMessage="Complete a one-time Strong Customer Authentication (SCA) with our trusted payment partner, Mangopay." />
        ),
        icon: faLock,
        checked: canListAndTrade,
      },
    statusTarget === FiatWalletAccountState.VALIDATED_OWNER && {
      title: <FormattedMessage defaultMessage="Add your ID" />,
      desc: (
        <FormattedMessage defaultMessage="Upload a government-issued ID to enable cash deposits, cash withdrawals, and cash rewards." />
      ),
      icon: faImage,
    },
  ].filter(Boolean);

  return (
    <Content>
      <HeadlineL as="h3">
        {!!fromListing && (
          <FormattedMessage defaultMessage="Confirm additional details" />
        )}
        {!!fromOnboarding && (
          <FormattedMessage defaultMessage="It's easy to activate your Cash Wallet" />
        )}
        {!!fromDepositAndWithdraw && (
          <FormattedMessage defaultMessage="It's easy to activate your Cash Wallet and verify your identity" />
        )}
      </HeadlineL>
      {!!fromListing && (
        <BodyM color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="To list cards and receive cash, you'll need to activate your Cash Wallet by providing some additional information." />
        </BodyM>
      )}
      {Steps.map((step, index) => (
        <Item
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          step={index + 1}
          title={step.title}
          desc={step.desc}
          icon={step.icon}
          checked={step.checked}
        />
      ))}
      <LabelS color="var(--c-nd-600)">
        <FormattedMessage
          defaultMessage="Cash Wallet is powered by Mangopay. Availability may vary depending on your country of residence.{br} <link>Learn more</link>"
          values={{
            link: (...text) => (
              <LinkInFormattedMessage
                target="_blank"
                rel="noreferrer"
                href={NOT_SUPPORTED_COUNTRIES_LINK}
              >
                {text}
              </LinkInFormattedMessage>
            ),
            br: <br />,
          }}
        />
      </LabelS>
      <Actions>
        <Button fullWidth color="primary" size="medium" onClick={onGetStarted}>
          <FormattedMessage
            {...(isFiatWalletOwner ? glossary.continue : glossary.getStarted)}
          />
        </Button>
      </Actions>
    </Content>
  );
};
